import type { ReactNode } from 'react'
import { activityRetentionLabel } from './activityRetention'
import { Pencil } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useViewOnly } from '@/components/templates/app-shell'
import { displayDate } from '@/lib/dates'
import { LastRun, NextRun } from './ConnectionCells'
import { connectionTypeName, unmatchedPath } from './types'
import { words, type ConnectionDetail } from './detailTypes'

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="flex h-9 items-center px-4 text-caption text-muted-foreground">{label}</dt>
      <dd className="flex h-9 min-w-0 items-center px-4 text-body-sm">{children}</dd>
    </>
  )
}
export function ConnectionFacts({
  row,
  onEdit,
  onErrors,
  onRetention,
}: {
  row: ConnectionDetail
  onEdit: () => void
  onErrors: () => void
  onRetention: () => void
}) {
  const viewOnly = useViewOnly()
  return (
    <div className="grid grid-cols-1 rounded-md border border-border bg-card md:grid-cols-2">
      <dl className="grid grid-cols-[max-content_1fr] content-start [&>*]:border-border [&>dd:not(:nth-child(2))]:border-t [&>dt:not(:first-child)]:border-t">
        <Fact label="Type">{connectionTypeName(row.connection_type)}</Fact>
        <Fact label="Source">{row.source_key ?? '—'}</Fact>
        <Fact label="Status">{words(row.status)}</Fact>
        <Fact label="Import history from">
          <span className="flex items-center gap-2">
            {row.import_from ? displayDate(row.import_from) : 'All history'}
            {row.status !== 'removed' && (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Edit import history from"
                disabled={viewOnly}
                onClick={onEdit}
              >
                <Pencil className="size-3.5" strokeWidth={1.5} />
              </Button>
            )}
          </span>
        </Fact>
        <Fact label="Activity retention">
          <span className="flex items-center gap-2">
            {activityRetentionLabel(row.activity_retention_years)}
            {!viewOnly &&
              row.status !== 'removed' &&
              row.activity_retention_years !== undefined && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Edit activity retention"
                  onClick={onRetention}
                >
                  <Pencil className="size-3.5" strokeWidth={1.5} />
                </Button>
              )}
          </span>
        </Fact>
      </dl>
      <dl className="grid grid-cols-[max-content_1fr] content-start md:border-l md:border-border [&>*]:border-border [&>dd:not(:nth-child(2))]:border-t [&>dt:not(:first-child)]:border-t">
        <Fact label="Schedule">Every 6 hours</Fact>
        <Fact label="Next run">
          <NextRun row={row} />
        </Fact>
        <Fact label="Last run">
          <LastRun row={row} />
        </Fact>
        <Fact label="Open unmatched">
          <Button variant="link" size="inline" asChild>
            <a href={`${unmatchedPath}?connection_id=${row.id}`}>{row.unmatched_count} unmatched</a>
          </Button>
        </Fact>
        <Fact label="Open errors">
          <Button variant="link" size="inline" onClick={onErrors}>
            {row.error_count} open
          </Button>
        </Fact>
      </dl>
    </div>
  )
}
